import React from "react";
import radium, { Style } from "radium";
import shallowCompare from "react-addons-shallow-compare";
import leaflet from "leaflet";
import assign from "object-assign";
import { browserHistory, Link } from "react-router";
import { color, zIndex } from "rizzo-next/sass/settings.json";
import { connect } from "react-redux";
import kebabCase from "lodash/kebabCase";
import xor from "lodash/xor";
import actions from "../../actions";
import { styles, scopedStyles, markerStyles, markerStylesMixin } from "./styles";
import icons from "./icons";

const _ = { kebabCase, xor };

export default class InteractiveMap extends React.Component {
  static setPoiUrl({ params, id }) {
    return `/${params.splat}/${params.resource}/${id}`;
  }

  constructor(props) {
    super(props);

    this.state = {
      popupBanner: {
        isVisible: false,
        name: "",
        subtype: "",
      },
      clickedMarker: {},
      currentPoiType: null,
      showAttribution: false,
    };

    this.mapSettings = {
      accessToken: "pk.eyJ1IjoibG9uZWx5cGxhbmV0IiwiYSI6Imh1ODUtdUEifQ.OLLon0V6rcoTyayXzzUzsg",
      attribution: `<span title="Map data &copy; OpenStreetMap contributors,
        CC-BY-SA, Imagery &copy; Mapbox">&copy;</span>`,
      maxZoom: 17,
      popupOptions: {
        className: "InteractiveMap-popup",
        closeButton: false,
        maxWidth: 150,
        offset: leaflet.point(0, 0),
        autoPan: false,
      },
      projectId: "lonelyplanet.b963d424",
      url: "https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}",
    };

    this.markerMapping = {
      sleeping: "sleep",
      drinking_nightlife: "drink",
      transport: "transport",
      activities: "see",
      tours: "see",
      entertainment: "play",
      shopping: "shop",
      eating: "eat",
      restaurants: "eat",
      sights: "see",
      info: "default",
      festivals_events: "play",
    };

    [
      "setMarkerIcon",
      "defineCustomIcons",
      "initializeMap",
      "initializeTileLayer",
      "initializeCenterMarker",
      "initializeMarkers",
      "initializePopup",
      "updateMarkerForHoverMode",
      "updateMarkerForExploreMode",
      "fitBounds",
      "toggleAttribution",
    ].forEach(fn => {
      this[fn] = this[fn].bind(this);
    });
  }

  componentWillMount() {
    const markerColors = {
      eat: color.poiEat,
      drink: color.poiDrink,
      play: color.poiPlay,
      see: color.poiSee,
      shop: color.poiShop,
      sleep: color.poiSleep,
      transport: color.poiTransport,
      default: color.poiDefault,
      center: color.blue,
    };

    Object.keys(markerColors).forEach((type) => {
      if (type === "center") {
        assign(markerStyles, {
          [`.leaflet-div-icon-${type}`]: markerStylesMixin(
            markerColors[type],
            this.props.mode
          ),
        });
      } else {
        assign(markerStyles, {
          [`.leaflet-div-icon-${type}`]: markerStylesMixin(
            markerColors[type],
            this.props.mode
          ),
          [`.leaflet-div-icon-${type}.is-active`]: markerStylesMixin(
            markerColors[type],
            this.props.mode,
            "active"
          ),
        });
      }
    });
  }

  componentDidMount() {
    const {
      center,
      centerName,
      places,
      hideCenterPin,
      mapId,
      markerClick,
      zoom,
      hideZoomControls,
    } = this.props;

    this.defineCustomIcons();
    this.initializeMap(mapId, center, zoom, hideZoomControls);

    this.initializeTileLayer();
    if (!hideCenterPin && center && centerName) {
      this.initializeCenterMarker(center, centerName);
    }

    this.initializeMarkers(places, markerClick);
  }

  componentWillReceiveProps(nextProps) {
    const {
      mode,
      activePoi,
      markerSize,
      markerClick,
    } = this.props;

    // Check for new POIs when:
    // - type changes
    // - list is filtered
    // - scroll to load more
    if (_.xor(nextProps.places, this.props.places).length) {
      this.leafletMap.removeLayer(this.layerGroup);
      this.initializeMarkers(nextProps.places, markerClick);
    }

    if (mode === "hover") {
      this.updateMarkerForHoverMode(nextProps);
    }

    if (mode === "explore" && activePoi.id !== nextProps.activePoi.id) {
      this.updateMarkerForExploreMode(activePoi, markerSize, nextProps);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillUpdate(nextProps, nextState) {
    if (this.props.mode === "explore") {
      const markerShouldUpdate = nextState.clickedMarker &&
        this.state.currentPoiType &&
        this.state.clickedMarker !== nextState.clickedMarker;

      if (markerShouldUpdate) {
        if (this.state.clickedMarker.target !== nextState.clickedMarker.target) {
          this.setMarkerIcon();
        } else {
          browserHistory.push(InteractiveMap.setPoiUrl(nextProps.activePoi));
        }
      }
    }
  }

  /**
   * Set the marker icon
   */
  setMarkerIcon(size = this.props.markerSize, active = false) {
    const marker = this.state.clickedMarker.target;

    if (!marker) return;

    this.layerGroup.eachLayer((layer) => {
      if (marker.options.id === layer.options.id) {
        const icon = layer.options.icon;
        const type = this.markerMapping[this.state.currentPoiType];

        icon.options.iconSize = size;
        icon.options.className = active ?
          `leaflet-div-icon-${type} is-active` :
          `leaflet-div-icon-${type}`;

        layer.setIcon(icon);
      }
    });
  }

  /**
   * Define custom icons for leaflet.marker
   */
  defineCustomIcons() {
    this.icons = {
      default: leaflet.divIcon({
        className: "leaflet-div-icon-default",
        iconSize: this.props.markerSize,
        html: icons.defaultIcon,
      }),

      center: leaflet.divIcon({
        className: "leaflet-div-icon-center",
        iconSize: [30, 30],
        html: icons.defaultIcon,
      }),

      eat: leaflet.divIcon({
        className: "leaflet-div-icon-eat",
        iconSize: this.props.markerSize,
        html: icons.eat,
      }),

      drink: leaflet.divIcon({
        className: "leaflet-div-icon-drink",
        iconSize: this.props.markerSize,
        html: icons.drink,
      }),

      play: leaflet.divIcon({
        className: "leaflet-div-icon-play",
        iconSize: this.props.markerSize,
        html: icons.play,
      }),

      see: leaflet.divIcon({
        className: "leaflet-div-icon-see",
        iconSize: this.props.markerSize,
        html: icons.see,
      }),

      shop: leaflet.divIcon({
        className: "leaflet-div-icon-shop",
        iconSize: this.props.markerSize,
        html: icons.shop,
      }),

      sleep: leaflet.divIcon({
        className: "leaflet-div-icon-sleep",
        iconSize: this.props.markerSize,
        html: icons.sleep,
      }),

      transport: leaflet.divIcon({
        className: "leaflet-div-icon-transport",
        iconSize: this.props.markerSize,
        html: icons.transport,
      }),
    };
  }

  /**
   * Initialize leaflet.map
   * @see http://leafletjs.com/reference.html#map-class
   * @param  {string}  id               ID in the DOM on which to attach the map to
   * @param  {array}   center           Initial geographical center of the map
   * @param  {number}  zoom             Initial map zoom
   * @param  {boolean} hideZoomControls Whether the zoom control is added to the map by default
   * @return {object}                   Instantiates a map object given a div element (or its id)
   *                                    and optionally an object literal with map options
   */
  initializeMap(id, center, zoom, hideZoomControls) {
    this.leafletMap = leaflet.map(id, {
      scrollWheelZoom: false,
      zoomControl: !hideZoomControls,
      attributionControl: false,
    });

    /**
     * Reset the state and clicked marker's size when the map is clicked on,
     * i.e. when a marker is clicked off of
     */
    this.leafletMap.on("click dragstart", () => {
      const newState = {
        showAttribution: false,
      };

      if (this.state.popupBanner.isVisible) {
        this.setMarkerIcon();

        assign(newState, {
          popupBanner: {
            isVisible: !this.state.popupBanner.isVisible,
            name: "",
            subtype: "",
          },
          clickedMarker: {},
          currentPoiType: null,
        });

        this.props.dispatch(actions.hoverPlace({}));
      }

      this.setState(newState);
    });
  }

  /**
   * Initialize leaflet.tileLayer
   * @see http://leafletjs.com/reference.html#tilelayer
   * @return {object} Instantiates a tile layer object given a URL template and optionally an
   *                  options object
   */
  initializeTileLayer() {
    leaflet.tileLayer(this.mapSettings.url, {
      accessToken: this.mapSettings.accessToken,
      attribution: this.mapSettings.attribution,
      id: this.mapSettings.projectId,
      maxZoom: this.mapSettings.maxZoom,
    }).addTo(this.leafletMap);
  }

  /**
   * Display a marker at the map's center; uses a large blue icon to denote
   * @see http://leafletjs.com/reference.html#marker
   * @return {object}
   */
  initializeCenterMarker(center, centerName) {
    this.centerMarker = leaflet.marker(center, {
      opacity: 1,
      icon: this.icons.center,
    }).bindPopup(centerName, {
      offset: leaflet.point(0, -5),
      autoPan: false,
    }).addTo(this.leafletMap);
  }

  /**
   * Create the map's markers from an array of places
   * @see http://leafletjs.com/reference.html#marker
   * @return {object} Instantiates a marker object given a geographical point and optionally an
   *                  options object
   */
  initializeMarkers(places, onClick) {
    this.bounds = [];

    this.markers = places.filter(p => p.location).map((poi) => {
      const coords = poi.location.coordinates.slice(0).reverse();

      this.bounds.push(coords);

      const leafletMarker = leaflet.marker(coords, {
        opacity: this.props.markerOpacity,
        icon: this.icons[this.markerMapping[poi.poiType.toLowerCase()]],
        id: `marker-${poi.id}`,
        riseOnHover: true,
      })
        .on("click", (event) => {
          this.setState({
            clickedMarker: event,
            currentPoiType: poi.poiType.toLowerCase(),
            showAttribution: false,
          });

          if (typeof onClick === "function") {
            onClick(event, poi);
          }
        })
        .on("mouseover", (event) => {
          if (this.props.mode === "hover") {
            event.target.openPopup();
            event.target.setOpacity(1);
            this.props.dispatch(actions.hoverPlace(poi));
          }
        })
        .on("mouseout", (event) => {
          if (this.props.mode === "hover") {
            event.target.closePopup();
            this.props.dispatch(actions.hoverPlace({}));
          }
        });

      if (this.props.mode !== "explore") {
        leafletMarker.bindPopup(poi.name, this.mapSettings.popupOptions);
      }

      return leafletMarker;
    });

    this.layerGroup = leaflet.layerGroup(this.markers).addTo(this.leafletMap);

    if (this.props.fitBounds) {
      this.fitBounds();
    }
  }

  /**
   * Create a map popup
   * @see http://leafletjs.com/reference.html#popup
   * @return {object} Instantiates a popup object given an optional options object that describes
   *                  its appearance, location and an optional source object that is used to tag the
   *                  popup with a reference to the ILayer to which it refers
   */
  initializePopup(latlng, content) {
    this.popup = leaflet.popup(this.mapSettings.popupOptions)
      .setLatLng(latlng.slice(0).reverse())
      .setContent(content)
      .openOn(this.leafletMap);

    if (this.props.panToCenter) {
      this.leafletMap.panTo(latlng);
    }
  }

  /**
   * Update and display the marker's popup when the map is in "hover" mode
   * @param {object} nextProps The new set of props recieved from componentWillReceiveProps
   */
  updateMarkerForHoverMode(nextProps) {
    const shouldInitializePopup = nextProps.activePoi.id &&
      nextProps.activePoi.name &&
      nextProps.activePoi.location &&
      nextProps.activePoi.location.coordinates.length;

    if (shouldInitializePopup) {
      this.initializePopup(nextProps.activePoi.location.coordinates, nextProps.activePoi.name);

      this.layerGroup.eachLayer((layer) => {
        const opacityShouldUpdate = `marker-${nextProps.activePoi.id}` === layer.options.id &&
          layer.options.opacity !== 1;

        if (opacityShouldUpdate) {
          layer.setOpacity(1);
        }
      });
    }

    if (!nextProps.activePoi.id) {
      this.leafletMap.closePopup();
    }
  }

  /**
   * Update and display the marker's popup as a banner at the top of the map
   * when the map is in "explore" mode for mobile
   * @param  {object} activePoi  Data for the "current" POI
   * @param  {array}  markerSize Width and height of the marker icon
   * @param  {object} nextProps  The new set of props recieved from componentWillReceiveProps
   */
  updateMarkerForExploreMode(activePoi, markerSize, nextProps) {
    this.setMarkerIcon();

    const shouldInitializePopup = nextProps.activePoi.id &&
      nextProps.activePoi.name &&
      nextProps.activePoi.location.coordinates.length;

    /**
     * Change the clicked marker's size and update the state to show the popup
     * banner
     */
    if (shouldInitializePopup) {
      this.setMarkerIcon([50, 50], true, true);

      this.setState({
        popupBanner: {
          isVisible: true,
          name: nextProps.activePoi.name,
          subtype: nextProps.activePoi.subtypes[0],
        },
      });

      if (this.props.panToCenter) {
        this.leafletMap.panTo(nextProps.activePoi.location.coordinates.slice(0));
      }

      this.layerGroup.eachLayer((layer) => {
        const zIndexShouldUpdate = `marker-${nextProps.activePoi.id}` === layer.options.id &&
          layer.options.zIndexOffset === 0;

        if (zIndexShouldUpdate) {
          layer.setZIndexOffset(zIndex.globalHeader);
        }
      });
    }

    /**
     * If the marker is already open, go to the POI on second click/tap; this
     * mimics the functionality in Guides app
     */
    // if (activePoi.id === nextProps.activePoi.id && this.state.popupBanner.isVisible) {
    //   browserHistory.push(InteractiveMap.setPoiUrl(activePoi));
    // }
  }

  fitBounds() {
    if (this.bounds.length) {
      this.leafletMap.fitBounds(this.bounds, {
        padding: [30, 30],
      });
    }
  }

  toggleAttribution() {
    this.setState({
      showAttribution: !this.state.showAttribution,
    });
  }

  render() {
    const {
      fill,
      height,
      mapId,
      mode,
      activePoi,
    } = this.props;

    return (
      <div
        className="InteractiveMap-container"
        style={[
          styles.container.base,
          fill && styles.container.fill,
          height && { height: `${height}px` },
        ]}
      >
        <Style
          scopeSelector=".InteractiveMap-container"
          rules={assign({}, scopedStyles, markerStyles)}
        />

        {mode === "explore" && activePoi.id && activePoi.params &&
          <div
            className="InteractiveMap-popupBanner"
            style={[
              styles.popupBanner.container.base,
              !this.state.popupBanner.isVisible &&
                styles.popupBanner.container.hidden,
              this.state.popupBanner.isVisible && this.state.popupBanner.name &&
                styles.popupBanner.container.visible,
            ]}
          >
            <Link
              to={InteractiveMap.setPoiUrl(activePoi)}
              style={styles.popupBanner.anchor.base}
            >
              <div
                className="InteractiveMap-popupBanner-name"
                style={styles.popupBanner.name.base}
              >
                {this.state.popupBanner.name}
              </div>

              <div
                className="InteractiveMap-popupBanner-subtype"
                style={styles.popupBanner.subtype.base}
              >
                {this.state.popupBanner.subtype}
              </div>
            </Link>
          </div>
        }

        <div
          className="InteractiveMap"
          id={mapId}
          style={styles.map.base}
        />

        <div
          className="InteractiveMap-attribution"
          style={styles.attribution.container.base}
        >
          <button
            onClick={this.toggleAttribution}
            style={styles.attribution.button.base}
          >
            Attribution
          </button>
          <div
            onClick={this.toggleAttribution}
            style={[
              styles.attribution.content.base,
              this.state.showAttribution ?
                styles.attribution.content.visible :
                styles.attribution.content.hidden,
            ]}
          >
            <a href="http://leafletjs.com/">Leaflet</a> | Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="http://mapbox.com">Mapbox</a>
          </div>
        </div>
      </div>
    );
  }
}

InteractiveMap.propTypes = {
  /**
   * Lat and long coordinates for the initial geographical center of the map
   */
  center: React.PropTypes.array.isRequired,

  /**
   * An array of places to plot on the map as markers
   */
  places: React.PropTypes.array.isRequired,

  /**
   * Name of the POI at the center of the map
   */
  centerName: React.PropTypes.string,

  /**
   * Allow the map to fill its parent's dimentions
   */
  fill: React.PropTypes.bool,

  /**
   * Should the pin/marker for the centerpoint be hidden
   */
  hideCenterPin: React.PropTypes.bool,

  /**
   * Data for the current POI; expects:
   *
   * coords  Array  Lat and long coodinates of the marker that is currently active
   * name    String Name of the marker that is currently active
   * id      Number ID of POI that's selected
   * type    String Main type of the POI
   * subtype String Subtype classification of the POI
   */
  activePoi: React.PropTypes.object,

  /**
   * A height (in pixels) to constrain the map to
   */
  height: React.PropTypes.number,

  /**
   * ID to initialize the map
   */
  mapId: React.PropTypes.string,

  /**
   * Tell the map how it should behave when controlled by an outside component
   *
   * - hover: a list item is hovered and the corresponding marker popup's visibility is toggled
   * - explore: full-screen experience for mobile devices
   */
  mode: React.PropTypes.oneOf([
    "",
    "hover",
    "explore",
  ]),

  /**
   * Method to run when the marker receives the click event
   */
  markerClick: React.PropTypes.func,

  /**
   * Width and height for map's marker size
   */
  markerSize: React.PropTypes.array,

  /**
   * Opacity value for marker from 0 to 1.
   */
  markerOpacity: React.PropTypes.number,

  /**
   * Initial zoom level for the map
   */
  zoom: React.PropTypes.number,

  /**
   * Whether or not to hide the map's zoom controls
   */
  hideZoomControls: React.PropTypes.bool,

  /**
   * Whether or not to pan the map to a new centerpoint
   */
  panToCenter: React.PropTypes.bool,

  /**
   * Used to send an action for the map markers
   */
  dispatch: React.PropTypes.func,

  /**
   * Sets a map view that contains the given geographical bounds with the
   * maximum zoom level possible
   */
  fitBounds: React.PropTypes.bool,
};

InteractiveMap.defaultProps = {
  center: [],

  centerName: "",

  places: [],

  fill: false,

  hideCenterPin: false,

  activePoi: {},

  height: null,

  mapId: "leaflet-map-container",

  mode: "",

  markerClick: null,

  markerSize: [20, 20],

  markerOpacity: 1,

  zoom: 18,

  hideZoomControls: false,

  panToCenter: false,

  dispatch: null,

  fitBounds: false,
};

InteractiveMap.styles = styles;

const mapStateToProps = (state) => ({
  activePoi: state.nearbyPois.active || {},
});

const connected = connect(mapStateToProps)(radium(InteractiveMap));
export { connected };



// WEBPACK FOOTER //
// ./assets/pois/components/interactiveMap/index.jsx